import { Controller } from "@hotwired/stimulus"

const FORMATTERS = {
  spaced: (v) => v.toLocaleString("en-US").replace(/,/g, " "),
}

export default class extends Controller {
  static targets = ["number", "bar", "node", "line"]
  static values = {
    counting: String,                           // class added on connect (animation start state)
    shown: String,                              // class added when trend nodes fade in
    duration: { type: Number, default: 1200 },
    stagger: { type: Number, default: 160 },
  }

  connect() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    this.timeouts = []
    this.element.classList.add(this.countingValue)

    this.numberTargets.forEach((el) => {
      el.dataset.countUpEnd = parseInt(el.textContent.replace(/\D/g, ""), 10) || 0
      el.textContent = this.format(el, 0)
    })

    this.barTargets.forEach((el) => {
      el.style.height = "0px"
    })

    this.observer = new IntersectionObserver((entries) => this.onIntersect(entries), { threshold: 0.35 })
    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer?.disconnect()
    this.timeouts?.forEach(clearTimeout)
  }

  defer(fn, delay) {
    this.timeouts.push(setTimeout(fn, delay))
  }

  onIntersect(entries) {
    if (!entries.some((entry) => entry.isIntersecting)) return

    this.observer.disconnect()
    this.numberTargets.forEach((el, index) => {
      this.defer(() => this.animate(el, this.barTargets[index]), index * this.staggerValue)
    })

    const countDone = (this.numberTargets.length - 1) * this.staggerValue + this.durationValue
    this.defer(() => this.revealTrend(), countDone)
  }

  revealTrend() {
    const step = 220
    this.nodeTargets.forEach((el, index) => this.defer(() => el.classList.add(this.shownValue), index * step))
    this.lineTargets.forEach((el, index) => this.defer(() => el.classList.add(this.shownValue), index * step + step / 2))
    this.defer(() => this.resetTrend(), this.nodeTargets.length * step + 4000)
  }

  resetTrend() {
    this.nodeTargets.forEach((el) => el.classList.remove(this.shownValue))
    this.lineTargets.forEach((el) => el.classList.remove(this.shownValue))
    this.defer(() => this.revealTrend(), 700)
  }

  animate(el, bar) {
    const end = +el.dataset.countUpEnd
    const barTo = bar ? +bar.dataset.countUpBarTo : 0
    const start = performance.now()

    const step = (now) => {
      const progress = Math.min((now - start) / this.durationValue, 1)
      const eased = 1 - (1 - progress) ** 5
      el.textContent = this.format(el, Math.round(end * eased))
      if (bar) bar.style.height = `${barTo * eased}px`
      if (progress < 1) requestAnimationFrame(step)
    }

    requestAnimationFrame(step)
  }

  format(el, value) {
    return (FORMATTERS[el.dataset.countUpFormat] || String)(value)
  }
}
